import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { logSwallow } from "../../lib/log";

/**
 * TranslationUsage — admin widget showing this month's content-translation
 * spend against the hard cap, plus request / cache / skip counters.
 */
export default function TranslationUsage() {
  const { t } = useTranslation();
  const [u, setU] = useState(null);

  const load = () =>
    api.get("/admin/translation-usage")
      .then(({ data }) => setU(data))
      .catch((e) => logSwallow("admin.txUsage", e));

  useEffect(() => { load(); }, []);

  if (!u) return <p className="text-zinc-500">{t("admin.loading")}</p>;

  const pct = Math.min(100, u.pct_used || 0);
  const barColor = pct >= 100 ? "#FF1A1A" : pct >= 80 ? "#F59E0B" : "#E60000";

  const stats = [
    [t("admin.tx.requests"), u.requests ?? 0, "tx-requests"],
    [t("admin.tx.cacheHits"), u.cache_hits ?? 0, "tx-cache-hits"],
    [t("admin.tx.skipped"), u.skipped_same_lang ?? 0, "tx-skipped"],
    [t("admin.tx.apiCalls"), u.calls ?? 0, "tx-calls"],
  ];

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="admin-translation-usage">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages size={18} className="text-[#E60000]" />
          <h3 className="font-display text-xl">{t("admin.tx.title")}</h3>
        </div>
        <button onClick={load} data-testid="tx-usage-refresh" className="text-zinc-400 hover:text-white" aria-label="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="font-display text-3xl" data-testid="tx-spend">${(u.est_cost_usd || 0).toFixed(2)}</div>
          <div className="text-xs text-zinc-500 font-mono-accent mt-1">
            {t("admin.tx.spend")} · {t("admin.tx.cap")} ${Number(u.cap_usd).toFixed(0)}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl" style={{ color: barColor }} data-testid="tx-pct">{pct.toFixed(1)}%</div>
          <div className="text-xs text-zinc-500 font-mono-accent mt-1">{t("admin.tx.capUsed")}</div>
        </div>
      </div>

      <div className="mt-3 h-2.5 rounded-full bg-white/10 overflow-hidden" data-testid="tx-cap-bar">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      {u.cap_reached && (
        <p className="mt-2 text-xs font-mono-accent text-[#FF1A1A]" data-testid="tx-paused">{t("admin.tx.paused")}</p>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3">
        {stats.map(([label, val, tid]) => (
          <div key={tid} className="rounded-xl bg-black/40 p-3" data-testid={tid}>
            <div className="font-display text-2xl">{val}</div>
            <div className="text-[11px] text-zinc-500 font-mono-accent mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <p className="mt-4 text-[11px] text-zinc-600 font-mono-accent">
        {t("admin.tx.model")}: {u.model} · {t("admin.tx.month")} {u.month}
      </p>
    </div>
  );
}
